import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PregnancyReminderDialog } from "@/components/PregnancyReminderDialog";
import { Baby, CalendarClock, CheckCircle2, Mail } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine,
  ReferenceArea, CartesianGrid, Legend,
} from "recharts";

// Banda IOM (BMI normale): guadagno atteso ~0.5-2 kg nel I trimestre, poi 0.35-0.5 kg/sett
const expectedGain = (w) => {
  if (w <= 13) return [0, 2];
  return [Math.round((0.5 + (w - 13) * 0.35) * 10) / 10, Math.round((2 + (w - 13) * 0.5) * 10) / 10];
};

export const PregnancyCurve = ({ fiscalCode, patientName }) => {
  const [data, setData] = useState(null);
  const [marking, setMarking] = useState(null); // {id, done_at, note}
  const [remindOpen, setRemindOpen] = useState(false);
  const load = () => api.get("/patients/pregnancy-curve", { params: { fiscal_code: fiscalCode } })
    .then((r) => setData(r.data)).catch(() => setData({ points: [] }));
  useEffect(() => { load(); }, [fiscalCode]); // eslint-disable-line react-hooks/exhaustive-deps
  const markDone = async () => {
    try {
      await api.post("/patients/pregnancy-checks", { fiscal_code: fiscalCode, check_id: marking.id, done_at: marking.done_at, note: marking.note });
      toast.success("Controllo segnato come eseguito"); setMarking(null); load();
    } catch { toast.error("Operazione non riuscita"); }
  };
  const unmark = async (id) => {
    try { await api.delete(`/patients/pregnancy-checks/${id}`, { params: { fiscal_code: fiscalCode } }); load(); }
    catch { toast.error("Operazione non riuscita"); }
  };
  if (!data || !data.points?.length) return null;
  const th = data.thresholds || {};
  const pts = data.points.map((p) => {
    const [lo, hi] = expectedGain(p.week);
    return { ...p, gain_lo: lo, gain_hi: hi };
  });
  const alerts = pts.filter((p) => (p.systolic >= th.systolic_alert) || (p.diastolic >= th.diastolic_alert));
  const hasBP = pts.some((p) => p.systolic != null);
  const hasW = pts.some((p) => p.weight_gain != null);
  const hasKg = pts.some((p) => p.weight_kg != null);
  const domain = [Math.max(4, pts[0].week - 2), Math.min(42, pts[pts.length - 1].week + 4)];
  const est = data.estimate;
  const reminders = (data.reminders || []).filter((r) => r.status !== "later");
  const hasPending = reminders.some((r) => r.status === "overdue" || r.status === "due");
  const STATUS = {
    overdue: { cls: "bg-red-100 text-red-800 border-red-200", label: "In ritardo" },
    due: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Da eseguire ora" },
    upcoming: { cls: "bg-blue-100 text-blue-800 border-blue-200", label: "Prossimo" },
    done: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Eseguito" },
  };
  const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

  return (
    <>
    {est && (
      <Card className="p-4 rounded-2xl border-slate-200 bg-white shadow-sm" data-testid="pregnancy-reminders">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><CalendarClock className="h-4 w-4" /></div>
            <div>
              <div className="font-heading font-bold text-slate-900">Promemoria controlli</div>
              <div className="text-xs text-slate-500" data-testid="pregnancy-estimate">
                Epoca stimata oggi: <b>{est.estimated_week}ª settimana</b> · ultima visita alla {est.last_visit_week}ª · data presunta parto <b>{new Date(est.edd).toLocaleDateString("it-IT")}</b>
              </div>
            </div>
          </div>
          {hasPending && (
            <Button size="sm" variant="outline" className="h-8 bg-white border-pink-200 text-pink-700 hover:bg-pink-50"
                    onClick={() => setRemindOpen(true)} data-testid="pregnancy-remind-btn">
              <Mail className="h-3.5 w-3.5 mr-1" /> Promemoria alla paziente
            </Button>
          )}
        </div>
        {reminders.length === 0 ? (
          <div className="text-sm text-slate-500">Nessun controllo previsto nelle prossime settimane.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {reminders.map((r) => (
              <li key={r.id} className="py-2" data-testid={`pregnancy-reminder-${r.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span>
                  <span className="text-sm text-slate-800 flex-1 min-w-[200px]">{r.label}</span>
                  <span className="text-xs text-slate-500">sett. {r.week_from}-{r.week_to} · {fmt(r.date_from)} – {fmt(r.date_to)}</span>
                  {r.status === "done" ? (
                    r.manual && (
                      <button type="button" className="text-[11px] text-slate-500 underline" onClick={() => unmark(r.id)}
                              data-testid={`pregnancy-unmark-${r.id}`}>annulla</button>
                    )
                  ) : (
                    <button type="button" className="text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1 hover:underline"
                            onClick={() => setMarking({ id: r.id, done_at: new Date().toISOString().slice(0, 10), note: "" })}
                            data-testid={`pregnancy-mark-${r.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Segna eseguito
                    </button>
                  )}
                </div>
                {r.manual && (r.done_at || r.note) && (
                  <div className="mt-1 text-[11px] text-emerald-800 pl-1" data-testid={`pregnancy-done-info-${r.id}`}>
                    Eseguito il {r.done_at ? new Date(r.done_at).toLocaleDateString("it-IT") : "—"}{r.note ? ` · ${r.note}` : ""}
                  </div>
                )}
                {marking?.id === r.id && (
                  <div className="mt-2 flex items-end gap-2 flex-wrap bg-emerald-50/60 border border-emerald-100 rounded-lg p-2" data-testid={`pregnancy-mark-form-${r.id}`}>
                    <div>
                      <label className="text-[11px] text-slate-600">Data esecuzione</label>
                      <Input type="date" value={marking.done_at} onChange={(e) => setMarking({ ...marking, done_at: e.target.value })}
                             className="h-8 w-40 bg-white" data-testid="pregnancy-mark-date" />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-[11px] text-slate-600">Nota / esito (facoltativo)</label>
                      <Input value={marking.note} onChange={(e) => setMarking({ ...marking, note: e.target.value })}
                             placeholder="Es. negativo, referto allegato…" className="h-8 bg-white" data-testid="pregnancy-mark-note" />
                    </div>
                    <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={markDone} data-testid="pregnancy-mark-save">Salva</Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setMarking(null)}>Annulla</Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <PregnancyReminderDialog open={remindOpen} onOpenChange={setRemindOpen} fiscalCode={fiscalCode} patientName={patientName || "paziente"} />
      </Card>
    )}
    <Card className="p-4 rounded-2xl border-pink-200 bg-pink-50/40 shadow-sm" data-testid="pregnancy-curve">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-pink-100 text-pink-700 flex items-center justify-center"><Baby className="h-4 w-4" /></div>
          <div>
            <div className="font-heading font-bold text-slate-900">Curva gravidanza</div>
            <div className="text-xs text-slate-500">{pts.length} controlli · PA e incremento ponderale per settimana</div>
          </div>
        </div>
        {alerts.length > 0 ? (
          <span className="text-xs font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-3 py-1" data-testid="pregnancy-curve-alert">
            {alerts.length} controllo/i con PA ≥ {th.systolic_alert}/{th.diastolic_alert}
          </span>
        ) : hasBP ? (
          <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1">PA sempre sotto soglia</span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {hasBP && (
          <div className="bg-white rounded-xl border border-slate-200 p-2" data-testid="pregnancy-bp-chart">
            <div className="text-xs font-semibold text-slate-700 px-2 pt-1">Pressione arteriosa (mmHg)</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={pts} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week" type="number" domain={domain} tick={{ fontSize: 11 }} label={{ value: "sett.", position: "insideBottomRight", fontSize: 10 }} />
                <YAxis domain={[50, 190]} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(w) => `Settimana ${w}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceArea y1={th.systolic_severe} y2={190} fill="#fee2e2" fillOpacity={0.6} />
                <ReferenceLine y={th.systolic_alert} stroke="#dc2626" strokeDasharray="4 4" label={{ value: `${th.systolic_alert} sist.`, fontSize: 10, fill: "#dc2626", position: "right" }} />
                <ReferenceLine y={th.diastolic_alert} stroke="#f97316" strokeDasharray="4 4" label={{ value: `${th.diastolic_alert} diast.`, fontSize: 10, fill: "#f97316", position: "right" }} />
                <Line type="monotone" dataKey="systolic" name="Sistolica" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                <Line type="monotone" dataKey="diastolic" name="Diastolica" stroke="#0891b2" strokeWidth={2} dot={{ r: 4 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {hasW && (
          <div className="bg-white rounded-xl border border-slate-200 p-2" data-testid="pregnancy-weight-chart">
            <div className="text-xs font-semibold text-slate-700 px-2 pt-1">Incremento ponderale cumulativo (kg) · banda attesa IOM</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={pts} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week" type="number" domain={domain} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(w) => `Settimana ${w}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="gain_hi" name="Atteso max" stroke="#a7f3d0" strokeWidth={1} dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="gain_lo" name="Atteso min" stroke="#a7f3d0" strokeWidth={1} dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="weight_gain" name="Incremento" stroke="#db2777" strokeWidth={2} dot={{ r: 4 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {hasKg && (
          <div className="bg-white rounded-xl border border-slate-200 p-2" data-testid="pregnancy-kg-chart">
            <div className="text-xs font-semibold text-slate-700 px-2 pt-1">Peso assoluto (kg)</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={pts} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week" type="number" domain={domain} tick={{ fontSize: 11 }} />
                <YAxis domain={["dataMin - 3", "dataMax + 3"]} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(w) => `Settimana ${w}`} />
                <Line type="monotone" dataKey="weight_kg" name="Peso (kg)" stroke="#7c3aed" strokeWidth={2} dot={{ r: 4 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
    </>
  );
};
